import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { ChangeDnaData } from "src/app/api/models/change-dna-data";
import { APP_CONFIG } from "src/assets/config/app-config";
import { JoinPipe } from "src/app/pipe/pipe-join";

@Component({
  selector: "app-dna-canvas",
  templateUrl: "./dna-canvas.component.html",
  styleUrls: ["./dna-canvas.component.scss"],
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, JoinPipe],
})
export class DnaCanvasComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() data: {
    dnaData?: ChangeDnaData;
    timestamp?: string;
    id?: string;
  } = {};
  @Input() editMode = false;
  @Output() dnaDataChange = new EventEmitter<ChangeDnaData>();

  isDNAEnabled = APP_CONFIG.isDNAEnabled;
  editableData: { [key: string]: string } = {};

  @ViewChild("dnacanvas") dnacanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild("dnaCanvasWrapper") dnaCanvasWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild("dnaDataWrapper") dnaDataWrapper!: ElementRef<HTMLDivElement>;

  private ctx: CanvasRenderingContext2D | null = null;
  private animationId: number | null = null;
  private resizeObserver: ResizeObserver | null = null;

  private dnaCanvasProperties = {
    width: 400,
    height: 600,
    targets: [] as number[],
    R: 0,
    ii: 10,
    r: 0,
    deg: 0,
    rotate_speed: 0,
    distance: 0,
    lineWidth: 1,
    num: 0,
    offsetX: 0,
    offsetY: 0,
    color: "#0096e9",
    colorLine: "#a8a8a8",
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editMode'] && changes['editMode'].currentValue === true) {
      this.initEditableData();
    }
  }

  ngAfterViewInit() {
    this.setupResizeObserver();
    this.initializeCanvas();
  }

  ngOnDestroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private setupResizeObserver() {
    if (!window.ResizeObserver) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.updateCanvasSize();
    });

    if (this.dnaDataWrapper?.nativeElement) {
      this.resizeObserver.observe(this.dnaDataWrapper.nativeElement);
    }
  }

  private updateCanvasSize() {
    if (!this.dnaDataWrapper?.nativeElement) return;

    const contentHeight = this.dnaDataWrapper.nativeElement.scrollHeight;
    const minHeight = 400;
    const newHeight = Math.max(contentHeight, minHeight);

    if (newHeight !== this.dnaCanvasProperties.height) {
      this.dnaCanvasProperties.height = newHeight;
      this.initializeCanvas();
    }
  }

  private initializeCanvas() {
    if (!this.dnacanvas?.nativeElement) return;

    const canvas = this.dnacanvas.nativeElement;
    this.ctx = canvas.getContext("2d");

    if (!this.ctx) return;

    this.calculateCanvasProperties();
    this.setupCanvas();
    this.generateTargets();
    this.startAnimation();
  }

  private calculateCanvasProperties() {
    this.dnaCanvasProperties.R = this.dnaCanvasProperties.width / 10;
    this.dnaCanvasProperties.r =
      (this.dnaCanvasProperties.R / this.dnaCanvasProperties.ii / 4) * 3;
    this.dnaCanvasProperties.deg =
      Math.PI / (this.dnaCanvasProperties.ii * 1.5);
    this.dnaCanvasProperties.rotate_speed = this.dnaCanvasProperties.deg / 40;
    this.dnaCanvasProperties.distance = this.dnaCanvasProperties.r * 4;
    this.dnaCanvasProperties.num = Math.floor(
      (this.dnaCanvasProperties.height / this.dnaCanvasProperties.distance) * 1
    );
    this.dnaCanvasProperties.offsetX = this.dnaCanvasProperties.width / 2;
    this.dnaCanvasProperties.offsetY =
      this.dnaCanvasProperties.height / 2 -
      (this.dnaCanvasProperties.num * this.dnaCanvasProperties.distance) / 2;
  }

  private setupCanvas() {
    const canvas = this.dnacanvas.nativeElement;
    canvas.width = this.dnaCanvasProperties.width;
    canvas.height = this.dnaCanvasProperties.height;
    canvas.style.width = `${this.dnaCanvasProperties.width}px`;
    canvas.style.height = `${this.dnaCanvasProperties.height}px`;
  }

  private generateTargets() {
    this.dnaCanvasProperties.targets = [];
    for (let i = 1; i < this.dnaCanvasProperties.num; i++) {
      this.dnaCanvasProperties.targets.push(this.dnaCanvasProperties.deg * i);
      this.dnaCanvasProperties.targets.push(
        i * this.dnaCanvasProperties.distance
      );
    }
  }

  private dnaTransformPos(deg: number) {
    const x = Math.sin(deg) * this.dnaCanvasProperties.R;
    return { x };
  }

  private dnaDraw() {
    if (!this.ctx) return;

    this.ctx.beginPath();
    this.ctx.lineWidth = this.dnaCanvasProperties.lineWidth;
    this.ctx.strokeStyle = this.dnaCanvasProperties.colorLine;
    this.ctx.fillStyle = this.dnaCanvasProperties.color;

    for (let i = 0; i < this.dnaCanvasProperties.targets.length; i += 2) {
      const pos = this.dnaTransformPos(this.dnaCanvasProperties.targets[i]);
      const y =
        this.dnaCanvasProperties.offsetY +
        this.dnaCanvasProperties.targets[i + 1];

      // Draw connecting line
      this.ctx.moveTo(pos.x + this.dnaCanvasProperties.offsetX, y);
      this.ctx.lineTo(-pos.x + this.dnaCanvasProperties.offsetX, y);

      // Draw left circle
      this.ctx.moveTo(pos.x + this.dnaCanvasProperties.offsetX, y);
      this.ctx.arc(
        pos.x + this.dnaCanvasProperties.offsetX,
        y,
        this.dnaCanvasProperties.r,
        0,
        Math.PI * 2
      );

      // Draw right circle
      this.ctx.moveTo(-pos.x + this.dnaCanvasProperties.offsetX, y);
      this.ctx.arc(
        -pos.x + this.dnaCanvasProperties.offsetX,
        y,
        this.dnaCanvasProperties.r,
        0,
        Math.PI * 2
      );
    }

    this.ctx.stroke();
    this.ctx.fill();
    this.ctx.closePath();
  }

  private dnaUpdate() {
    for (let i = 0; i < this.dnaCanvasProperties.targets.length; i += 2) {
      if (this.dnaCanvasProperties.targets[i] !== undefined) {
        this.dnaCanvasProperties.targets[i] +=
          this.dnaCanvasProperties.rotate_speed;
        if (this.dnaCanvasProperties.targets[i] >= Math.PI) {
          this.dnaCanvasProperties.targets[i] -= Math.PI;
        }
      }
    }
  }

  private startAnimation() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    const animate = () => {
      if (!this.ctx) return;

      this.ctx.clearRect(
        0,
        0,
        this.dnaCanvasProperties.width,
        this.dnaCanvasProperties.height
      );
      this.dnaDraw();
      this.dnaUpdate();

      this.animationId = requestAnimationFrame(animate);
    };

    animate();
  }

  @HostListener("window:resize")
  onResize() {
    // ResizeObserver will handle the resize
  }

  /**
   * Get DNA data entries as typed array for template iteration
   * Eliminates need for $any() cast in template
   */
  get dnaDataEntries(): { key: string; value: string[] }[] {
    if (!this.data?.dnaData) return [];
    const dnaData = this.data.dnaData as Record<string, string[] | null | undefined>;
    return Object.entries(dnaData)
      .filter(([, value]) => Array.isArray(value))
      .map(([key, value]) => ({ key, value: value as string[] }));
  }

  /**
   * TrackBy function for DNA data entries
   */
  trackByKey = (_index: number, item: { key: string }): string => item.key;

  /**
   * Initialize editable data when edit mode is activated
   */
  initEditableData(): void {
    if (!this.data?.dnaData) return;

    this.editableData = {};
    const dnaData = this.data.dnaData as Record<string, string[] | null | undefined>;
    for (const key of Object.keys(dnaData)) {
      const value = dnaData[key];
      this.editableData[key] = Array.isArray(value) ? value.join(', ') : '';
    }
  }

  /**
   * Handle text change in edit mode
   */
  onTextChange(key: string, event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.editableData[key] = target.value;
    this.emitChanges();
  }

  /**
   * Emit changes to parent component
   */
  private emitChanges(): void {
    // Convert back to array format
    const updatedData: Partial<ChangeDnaData> = {};
    for (const key of Object.keys(this.editableData)) {
      const value = this.editableData[key];
      (updatedData as Record<string, string[]>)[key] = value
        .split(',')
        .map((s: string) => s.trim())
        .filter((s: string) => s);
    }
    this.dnaDataChange.emit(updatedData as ChangeDnaData);
  }
}
